import { VARIANT_TABLE_COLUMNS } from "@/settings/settings.ts";

import { type User } from "../graphql.js";
import { type ITableColumn } from "../redux/modules/table-data.js";
import { traverseAndCollect } from "../ui-modules/core/grid/column-props-factory.js";

// filterExpression property contains rsql expressions, we only have to join them with an AND operator (or ";" char)
export function getFilterExpression(columns: ITableColumn[]): string {
  return (
    columns &&
    columns
      .filter((curr: ITableColumn) => curr.filterExpression && curr.filterExpression !== "")
      .map((curr: ITableColumn) => curr.filterExpression)
      .join(";")
  );
}

/**
 * Matches a user's username, first name and last name against a search keyword. The search is case-insensitive.
 * @param user the user object whose fields are being searched
 * @param searchString The search string after the @ sign of the tag expression (i.e. 'volk' in case of @volk)
 * @returns true if the user matches, false otherwise
 */
export function taggedUserIncludes(user: User, searchString: string): boolean {
  if (!searchString || searchString.length === 0) {
    return true;
  }

  const searchStringLower: string = searchString.toLocaleLowerCase();

  return (
    user.username.toLowerCase().startsWith(searchStringLower) ||
    (!!user.firstName && user.firstName.toLowerCase().startsWith(searchStringLower)) ||
    (!!user.lastName && user.lastName.toLowerCase().startsWith(searchStringLower))
  );
}

/**
 * Extracts the filter tokens to be combined together to filter against a list.
 * @param input the full expression as provided by the end user
 * @returns the list of tokens to match against the content to be filtered
 */
export function parseFilterExpression(input: string): string[] {
  if (input == undefined) {
    return [];
  }
  const multiWordsRegEx = new RegExp(/"[^"]*"/, "ig"); // spot the expression between the "" chars
  let multiWords: string[] = input.match(multiWordsRegEx);

  if (multiWords) {
    // take back the " chars from the found expressions
    multiWords = multiWords.map((mltiWords) => mltiWords.replace(new RegExp('"', "g"), ""));
  }

  // get the multi words expressions out of the next match search
  const inputWithoutExpression: string = input.replace(multiWordsRegEx, "");
  const singleWordsRegEx = new RegExp(/(^|\s*)\S+($|\s*)/, "ig"); // spot the single words
  const singleWords: string[] = inputWithoutExpression.match(singleWordsRegEx);

  // concat both matches and filter/trim
  const tokens: string[] = [].concat(multiWords).concat(singleWords);

  return tokens.filter((token) => token).map((token) => token.trim().toLowerCase());
}

export function isProfileFilterColumn(key: string): boolean {
  return (
    key === VARIANT_TABLE_COLUMNS.PROFILE ||
    key === VARIANT_TABLE_COLUMNS.VCF_PROFILE ||
    key?.endsWith(`.${VARIANT_TABLE_COLUMNS.PROFILE}`) ||
    key?.endsWith(`.${VARIANT_TABLE_COLUMNS.VCF_PROFILE}`)
  );
}

export function genericFilter(columnDefs: Partial<ITableColumn>[], entity: unknown, filter?: string | null) {
  if (!filter) {
    return true;
  }
  return columnDefs.find((colDef: Partial<ITableColumn>) => {
    const collectedValues = traverseAndCollect(entity, (colDef.filterValueGetter as string) ?? colDef.field);
    const filterableValues = Array.isArray(collectedValues) ? collectedValues : [collectedValues]; // always manage an array
    return collectedValues && filterableValues.find((v) => `${v}`.toLowerCase().includes(filter.toLowerCase()));
  });
}

export const matchFilter = (fieldValues: (string | null | undefined)[], terms: string[]) => {
  if (terms === null || terms.length === 0) {
    return true;
  }
  for (let i = 0; i < terms.length; ++i) {
    let foundTerm = false;
    for (let j = 0; j < fieldValues.length; ++j) {
      if (fieldValues[j] && fieldValues[j]?.toLowerCase().includes(terms[i])) {
        foundTerm = true;
        break;
      }
    }
    if (!foundTerm) {
      return false;
    }
  }
  return true;
};
